import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../contexts/UserContext";
import { createSupportInquiry } from "./firebaseFunctions";
import styles from "./SupportPage.module.css";

type SubmitState =
  | { type: "idle"; message: string }
  | { type: "success"; message: string }
  | { type: "error"; message: string };

type SupportCategory =
  | "usage"
  | "refund"
  | "enterprise"
  | "partnership"
  | "incident"
  | "compensation"
  | "abuse"
  | "other";

const MIN_MESSAGE_LENGTH = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CATEGORY_ORDER: SupportCategory[] = [
  "usage",
  "refund",
  "enterprise",
  "partnership",
  "incident",
  "compensation",
  "abuse",
  "other",
];

const QUERY_TYPE_TO_CATEGORY: Record<string, SupportCategory> = {
  usage: "usage",
  refund: "refund",
  enterprise: "enterprise",
  partnership: "partnership",
  incident: "incident",
  compensation: "compensation",
  abuse: "abuse",
  report: "abuse",
  policy: "abuse",
  other: "other",
};

const SupportPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const [searchParams] = useSearchParams();
  const currentLang = lang ?? "en";
  const isKorean = currentLang.toLowerCase().startsWith("ko");
  const userContext = useUser();
  const userId = userContext?.userDetails?.id ?? "Guest";
  const isGuest = userId === "Guest";

  const defaultCategoryFromQuery = useMemo(() => {
    const type = (searchParams.get("type") ?? "").trim().toLowerCase();
    return QUERY_TYPE_TO_CATEGORY[type] ?? "usage";
  }, [searchParams]);

  const [category, setCategory] = useState<SupportCategory>("usage");
  const [replyEmail, setReplyEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>({
    type: "idle",
    message: "",
  });

  useEffect(() => {
    setCategory(defaultCategoryFromQuery);
  }, [defaultCategoryFromQuery]);

  useEffect(() => {
    if (userId === "Guest") {
      setReplyEmail("");
      return;
    }
    setReplyEmail(userId);
  }, [userId]);

  const messageLength = message.trim().length;
  const replyEmailTrimmed = replyEmail.trim();
  const isMessageTooShort =
    messageLength > 0 && messageLength < MIN_MESSAGE_LENGTH;
  const isReplyEmailMissing = replyEmailTrimmed.length === 0;
  const isReplyEmailInvalid =
    replyEmailTrimmed.length > 0 && !EMAIL_REGEX.test(replyEmailTrimmed);

  const helperText = useMemo(() => {
    if (isGuest) {
      return t("support_page.helper.sign_in_required", {
        defaultValue: isKorean
          ? "문의 접수는 로그인 후 이용할 수 있습니다."
          : "Please sign in to submit a support inquiry.",
      });
    }

    if (isReplyEmailInvalid) {
      return t("support_page.helper.invalid_email", {
        defaultValue: isKorean
          ? "답변받을 이메일 주소를 올바르게 입력해 주세요."
          : "Please enter a valid reply email address.",
      });
    }

    if (isMessageTooShort) {
      return t("support_page.helper.message_too_short", {
        count: MIN_MESSAGE_LENGTH,
        defaultValue: isKorean
          ? `문의 내용은 최소 ${MIN_MESSAGE_LENGTH}자 이상 입력해 주세요.`
          : `Please enter at least ${MIN_MESSAGE_LENGTH} characters.`,
      });
    }

    return t("support_page.helper.ready", {
      defaultValue: isKorean
        ? "문의 내용과 고객 ID가 함께 고객지원 메일로 전달됩니다."
        : "Your inquiry and customer ID will be sent to customer support.",
    });
  }, [isGuest, isKorean, isMessageTooShort, isReplyEmailInvalid, t]);

  const refundHint = useMemo(() => {
    if (category !== "refund") {
      return "";
    }
    return t("support_page.refund_hint", {
      defaultValue: isKorean
        ? "환불 문의는 구매 계정, 구매일, 상품명, 주문번호를 함께 작성해 주세요."
        : "For refund inquiries, include account, purchase date, product name, and order number.",
    });
  }, [category, isKorean, t]);

  const abuseHint = useMemo(() => {
    if (category !== "abuse") {
      return "";
    }
    return t("support_page.abuse_hint", {
      defaultValue: isKorean
        ? "정책 위반 신고는 관련 계정, 콘텐츠 링크 또는 주문번호, 위반 유형, 확인 가능한 설명을 함께 작성해 주세요."
        : "For policy violation reports, include the related account, content link or order number, violation type, and a clear description.",
    });
  }, [category, isKorean, t]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isGuest) {
      setSubmitState({
        type: "error",
        message: t("support_page.errors.sign_in_before_submit", {
          defaultValue: isKorean
            ? "로그인 후 고객지원 문의를 접수해 주세요."
            : "Please sign in before submitting a support inquiry.",
        }),
      });
      return;
    }

    const trimmed = message.trim();
    if (trimmed.length < MIN_MESSAGE_LENGTH) {
      setSubmitState({
        type: "error",
        message: t("support_page.helper.message_too_short", {
          count: MIN_MESSAGE_LENGTH,
          defaultValue: isKorean
            ? `문의 내용은 최소 ${MIN_MESSAGE_LENGTH}자 이상 입력해 주세요.`
            : `Please enter at least ${MIN_MESSAGE_LENGTH} characters.`,
        }),
      });
      return;
    }

    if (!EMAIL_REGEX.test(replyEmailTrimmed)) {
      setSubmitState({
        type: "error",
        message: t("support_page.helper.invalid_email", {
          defaultValue: isKorean
            ? "답변받을 이메일 주소를 올바르게 입력해 주세요."
            : "Please enter a valid reply email address.",
        }),
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitState({ type: "idle", message: "" });
    try {
      const response = await createSupportInquiry(
        userId,
        trimmed,
        currentLang,
        category,
        replyEmailTrimmed,
      );
      setMessage("");
      setSubmitState({
        type: "success",
        message: t("support_page.success.submitted", {
          id: response.supportInquiryId,
          defaultValue: isKorean
            ? `문의가 접수되었습니다. 문의 ID: ${response.supportInquiryId}`
            : `Your inquiry has been submitted. Inquiry ID: ${response.supportInquiryId}`,
        }),
      });
    } catch (error: any) {
      setSubmitState({
        type: "error",
        message:
          error?.message ??
          t("support_page.errors.submit_failed", {
            defaultValue: isKorean
              ? "문의 접수 중 오류가 발생했습니다."
              : "Failed to submit support inquiry.",
          }),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1>
          {t("support_page.title", {
            defaultValue: isKorean ? "고객지원" : "Customer Support",
          })}
        </h1>
        <p className={styles.subtitle}>
          {t("support_page.subtitle", {
            defaultValue: isKorean
              ? "문의 내용을 남겨주시면 고객 ID와 함께 고객지원 메일로 전달됩니다."
              : "Your inquiry will be delivered to customer support with your customer ID.",
          })}
        </p>
        <div className={styles.heroActions}>
          <Link to={`/${currentLang}/support/chatbot`} className={styles.secondaryButton}>
            {t("support_page.actions.ai_chatbot", {
              defaultValue: isKorean ? "AI 상담 시작" : "Start AI Support Chat",
            })}
          </Link>
        </div>
      </section>

      <section className={styles.card}>
        <form className={styles.form} onSubmit={onSubmit}>
          <label className={styles.label} htmlFor="support-user-id">
            {t("support_page.fields.customer_id", {
              defaultValue: isKorean ? "고객 ID" : "Customer ID",
            })}
          </label>
          <input
            id="support-user-id"
            className={styles.input}
            value={userId}
            disabled
          />

          <label className={styles.label} htmlFor="support-reply-email">
            {t("support_page.fields.reply_email", {
              defaultValue: isKorean ? "답변받을 이메일" : "Reply Email",
            })}
          </label>
          <input
            id="support-reply-email"
            className={styles.input}
            type="email"
            value={replyEmail}
            onChange={(event) => setReplyEmail(event.target.value)}
            placeholder={
              t("support_page.placeholders.reply_email", {
                defaultValue: isKorean
                  ? "답변받을 이메일 주소를 입력해 주세요."
                  : "Enter the email address to receive replies.",
              })
            }
            disabled={isSubmitting || isGuest}
          />

          <label className={styles.label} htmlFor="support-category">
            {t("support_page.fields.category", {
              defaultValue: isKorean ? "문의 카테고리" : "Inquiry Category",
            })}
          </label>
          <select
            id="support-category"
            className={styles.input}
            value={category}
            onChange={(event) =>
              setCategory(event.target.value as SupportCategory)
            }
            disabled={isSubmitting || isGuest}
          >
            {CATEGORY_ORDER.map((item) => (
              <option key={item} value={item}>
                {t(`support_page.categories.${item}`, {
                  defaultValue: item,
                })}
              </option>
            ))}
          </select>

          <label className={styles.label} htmlFor="support-message">
            {t("support_page.fields.message", {
              defaultValue: isKorean ? "문의 내용" : "Inquiry Message",
            })}
          </label>
          <textarea
            id="support-message"
            className={styles.textarea}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={
              t("support_page.placeholders.message", {
                defaultValue: isKorean
                  ? "문의 내용을 입력해 주세요."
                  : "Please describe your issue or request.",
              })
            }
            rows={8}
            disabled={isSubmitting || isGuest}
          />

          {refundHint.length > 0 && (
            <p className={styles.helperText}>{refundHint}</p>
          )}
          {abuseHint.length > 0 && (
            <p className={styles.helperText}>{abuseHint}</p>
          )}
          <p className={styles.helperText}>{helperText}</p>

          {submitState.type !== "idle" && (
            <p
              className={
                submitState.type === "success"
                  ? styles.successText
                  : styles.errorText
              }
            >
              {submitState.message}
            </p>
          )}

          <div className={styles.actions}>
            {isGuest && (
              <Link to={`/${currentLang}/login`} className={styles.secondaryButton}>
                {t("support_page.actions.sign_in", {
                  defaultValue: isKorean ? "로그인하기" : "Sign In",
                })}
              </Link>
            )}
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={
                isSubmitting ||
                isGuest ||
                isMessageTooShort ||
                isReplyEmailMissing ||
                isReplyEmailInvalid
              }
            >
              {isSubmitting
                ? t("support_page.actions.sending", {
                    defaultValue: isKorean ? "전송 중..." : "Sending...",
                  })
                : t("support_page.actions.submit", {
                    defaultValue: isKorean ? "문의 접수" : "Submit Inquiry",
                  })}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default SupportPage;
